import { useEffect, useState } from 'react';
import { getSignupChannel, hasSession } from '@/services/session';
import { useSignupLegalName, useSignupSignInContact } from '../profile/signup-identity';
import { earliestMissingAnswerStep, rebuildSignInContact } from '../resume/earlier-answers';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import type { SignupStepId } from '../steps';
import { useSignupApi } from './useSignupApi';
import { useSignupNavigation } from './useSignupNavigation';

/** What a step does about the answers it needs: show itself, send the member back, or wait. */
export type EarlierAnswersGuard =
  | { kind: 'ready' }
  | { kind: 'redirect'; step: SignupStepId }
  | { kind: 'pending' };

const READY: EarlierAnswersGuard = { kind: 'ready' };
const PENDING: EarlierAnswersGuard = { kind: 'pending' };

/**
 * Whether `step` has every answer collected before it on this path (a reload
 * in a new tab, or blocked storage, can lose them). A missing answer sends the
 * member to the first step that collects it, with no error. When the only gap
 * is the sign-in contact and the tab is still signed in, a step after the code
 * reads the contact back from the member's record first, and the account step
 * is the fallback.
 */
export function useEarlierAnswersRedirect(step: SignupStepId): EarlierAnswersGuard {
  const { path } = useSignupNavigation();
  const api = useSignupApi();
  const legalName = useSignupLegalName();
  const signInContact = useSignupSignInContact();
  const programSelection = useSignupFlowStore((state) => state.programSelection);
  const birthDate = useSignupFlowStore((state) => state.birthDate);
  const sexAssignedAtBirth = useSignupFlowStore((state) => state.sexAssignedAtBirth);
  const profileAddress = useSignupFlowStore((state) => state.profileAddress);
  const target = earliestMissingAnswerStep(step, path, {
    programSelection,
    legalName,
    signInContact,
    birthDate,
    sexAssignedAtBirth,
    profileAddress,
  });
  // Not on the code step itself: a member who is signed in has no code to
  // enter, so a lost contact there goes back to the account step.
  const rebuilding = target === 'account' && step !== 'code' && hasSession();
  const [rebuildFailed, setRebuildFailed] = useState(false);

  useEffect(() => {
    if (!rebuilding) return;
    let live = true;
    setRebuildFailed(false);
    // A flow that started over while reading must not get this contact.
    const epoch = useSignupFlowStore.getState().flowEpoch;
    api.getSignedInContact().then(
      (contact) => {
        if (!live) return;
        const rebuilt = rebuildSignInContact(contact, getSignupChannel());
        const store = useSignupFlowStore.getState();
        if (rebuilt !== null && store.flowEpoch === epoch) store.setSignInContact(rebuilt);
        else setRebuildFailed(true);
      },
      () => {
        if (live) setRebuildFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [rebuilding, api]);

  if (target === null) return READY;
  if (rebuilding && !rebuildFailed) return PENDING;
  return { kind: 'redirect', step: target };
}
